import React, { useEffect, useRef, useState } from 'react';
import {
  CardNumberElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cx from 'classnames';
import { Formik } from 'formik';
import * as yup from 'yup';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { LinkButton } from '@magoosh/lib/button';
import { Form } from '@magoosh/lib/forms';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { bugsnagNotify } from 'utilities/bugsnag';
import { formatPrice } from 'utilities/money';

import * as api from '@payments/api';
import Confirmation from '@payments/components/confirmation';
import InstallmentsFormGroup from '@payments/components/form_groups/installments';
import PaymentMethodsFormGroup from '@payments/components/form_groups/payment_methods';
import UserFormGroup from '@payments/components/form_groups/user';
import CouponCodeInput from '@payments/components/inputs/coupon_code';
import ScrollToError from '@payments/components/scroll_to_error';
import SubmitPaymentButton from '@payments/components/submit_payment_button';
import EuTerms from '@payments/components/terms/eu';
import InstallmentsTerms from '@payments/components/terms/installments';
import UsTerms from '@payments/components/terms/us';
import { genericErrorMessage, PaymentError } from '@payments/errors';
import {
  CreateSubscriptionResponse,
  PaymentMethod,
  PaymentStyle,
  SubscriptionFormProps,
  SubscriptionFormValues
} from '@payments/types';
import * as utils from '@payments/utils';

import style from '@payments/style.module.scss';

const validationSchema = yup.object({
  cardName: yup
    .string()
    .label('Full Name')
    .when(['pricing', 'paymentMethod'], utils.shouldValidatePaymentInfo),
  email: yup.string().label('Email Address').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  password: yup.string().label('Password').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  passwordConfirmation: yup.string().label('Confirm Password').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  termsAndPolicyAccepted: yup.bool().oneOf([true], 'Terms of Use and Privacy Policy must be accepted'),
  zipCode: yup
    .string()
    .label('Billing ZIP')
    .when(['pricing', 'paymentMethod'], utils.shouldValidatePaymentInfo)
});

const SubscriptionFormGroups: React.FC<SubscriptionFormProps> = (props) => {
  const authenticityToken = useAuthenticityToken();
  const { setModal } = useModalContext();
  // Formik automatically sets isSubmitting to false when our async handleSubmit finishes which
  // leads to the submit button being enabled for a bit before the user is redirected. To ensure
  // that the submit button stays disabled we manage our own state.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingPricing, setIsCalculatingPricing] = useState(false);
  const [cartAbandonQueued, setCartAbandonQueued] = useState(false);

  // Use refs to keep track of current couponCode and zipCode values. This lets us access to
  // current value to request new pricing. Without the ref we get stale values captured in a
  // closure from debounce.
  const couponCodeRef = useRef(props.order.pricing.couponCode);
  const zipCodeRef = useRef(props.order.pricing.zipCode || '');

  const elements = useElements();
  const stripe = useStripe();
  const subscriptionPlan = props.order.subscriptionPlan;

  // Determine if Apple/Google Pay is available and set it up
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestMethod, setPaymentRequestMethod] = useState<string>(null);

  useEffect(() => {
    if (!stripe) return;
    if (paymentRequest) return;

    const checkPaymentRequestOptions = async () => {
      try {
        const pr: any = await api.createStripePaymentRequest(
          stripe,
          subscriptionPlan.name,
          props.order.pricing.total
        );

        pr.canMakePayment().then((result) => {
          if (result) {
            setPaymentRequest(pr);

            if (result.applePay) {
              setPaymentRequestMethod('Apple Pay');
            } else if (result.googlePay) {
              setPaymentRequestMethod('Google Pay');
            }
          }
        });
      } catch(exception) {
        // Swallow the error since there is no negative user impact except that Google and Apple Pay
        // will not be available. But still log.
        bugsnagNotify(exception.debug);
      }
    };

    checkPaymentRequestOptions();
  }, [stripe]);

  const initialValues: SubscriptionFormValues = {
    cardName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    paymentMethod: PaymentMethod.STRIPE,
    paymentStyle: PaymentStyle.FULLPAYMENT,
    planId: subscriptionPlan.id,
    pricing: props.order.pricing,
    signedIn: props.signedIn,
    termsAndPolicyAccepted: !props.isEuUser,
    zipCode: props.order.pricing.zipCode || ''
  };

  const setupPaymentRequestListeners = (amount: number, clientSecret: string, nextUrl: string) => {
    paymentRequest.update({
      total: { label: subscriptionPlan.name, amount: amount }
    });

    setIsSubmitting(false);
    paymentRequest.removeAllListeners();

    paymentRequest.on('paymentmethod', async (event) => {
      try {
        const confirmPaymentRequestResponse = await api.confirmStripePaymentRequest(
          stripe,
          clientSecret,
          event.paymentMethod.id,
          event.paymentMethod.type
        )

        event.complete('success');

        if (confirmPaymentRequestResponse.nextUrl) {
          window.location.assign(confirmPaymentRequestResponse.nextUrl);
        } else if (nextUrl) {
          window.location.assign(nextUrl);
        } else {
          // Should not get here!
          throw new PaymentError(
            genericErrorMessage,
            `Unexpectedly reached the end of setupPaymentRequestListeners in SubscriptionFormGroups with response ${JSON.stringify(confirmPaymentRequestResponse)}`
          );
        }
      } catch(exception) {
        event.complete('fail');
        throw exception
      }
    });

    // For Google Pay we can directly launch into the payment request flow
    if (paymentRequestMethod === 'Google Pay') {
      paymentRequest.show();
    }
    // For Apple we need a second click because they require the payment sheet be initiated
    // by a direct click without any async code (our subscription create API call) beforehand.
    else {
      presentFinishPaymentRequestModal();
    }
  };

  const handleFreeOrder = async (values) => {
    let valuesForSubmit = { ...values, authenticityToken };

    const response = await api.createSubscription(stripe, valuesForSubmit);

    if (response.nextUrl) {
      window.location.assign(response.nextUrl);
    } else {
      // Should not get here!
      throw new PaymentError(
        genericErrorMessage,
        `Unexpectedly reached the end of handleFreeOrder in SubscriptionFormGroups with values ${JSON.stringify(values)}`
      );
    }
  };

  const handlePaidOrder = async (values) => {
    let valuesForSubmit = { ...values, authenticityToken };

    if (values.paymentMethod === PaymentMethod.STRIPE && !values.stripePaymentMethod) {
      const card = elements.getElement(CardNumberElement);
      const response = await api.createStripePaymentMethod(stripe, card, values);
      valuesForSubmit = { ...valuesForSubmit, ...response };
    } else if (values.paymentMethod === PaymentMethod.ALIPAY) {
      const response = await api.createStripePaymentMethod(stripe, null, values);
      valuesForSubmit = { ...valuesForSubmit, ...response };
    }

    const response = await api.createSubscription(stripe, valuesForSubmit);
    if(response.actionRequired) {
      // This means Apple or Google Pay.
      setupPaymentRequestListeners(response.actionRequired.amount, response.actionRequired.clientSecret, response.nextUrl)
    } else if (response.nextUrl) {
      window.location.assign(response.nextUrl);
    } else {
      // Should not get here!
      throw new PaymentError(
        genericErrorMessage,
        `Unexpectedly reached the end of handlePaidOrder in SubscriptionFormGroups with values ${JSON.stringify(valuesForSubmit)}`
      );
    }
  };

  const handleOnSubmit = async (values: SubscriptionFormValues, { setErrors }) => {
    setIsSubmitting(true);

    try {
      // Something with Stripe integration did not get set up correctly.
      if (values.paymentMethod === PaymentMethod.STRIPE && (!stripe || !elements)) {
        throw new PaymentError(
          genericErrorMessage,
          '`stripe` or `elements` unexpectedly did not exist'
        );
      }

      // Free order, submit to Magoosh backend
      if (values.pricing.total <= 0) {
        await handleFreeOrder(values);
        return;
      } else {
        await handlePaidOrder(values);
        return;
      }
    } catch(exception) {
      setErrors(exception.errors);
      bugsnagNotify(`Payment Error: ${exception.debug || JSON.stringify(exception.errors)}`);
      setIsSubmitting(false);
    }
  };

  const presentFinishPaymentRequestModal = () => {
    setModal({
      header: `Finish With ${paymentRequestMethod}`,
      body: (
        <div>
          <div>Click below to complete your payment for {subscriptionPlan.name}.</div>

          <PaymentRequestButtonElement
            className={cx(style.walletButton, 'u-margin-V-l')}
            options={{ paymentRequest }}
          />

          <LinkButton className="text-body" onClick={() => setModal(null)}>
            <small>&lt;&lt; Select a different payment method</small>
          </LinkButton>
        </div>
      )
    })
  }

  const updatePricing = (couponCode, paymentMethod, zipCode) => {
    return api.getSubscriptionPricing(subscriptionPlan.id, couponCode, paymentMethod, zipCode)
  };

  const handleUserFormBlur = (values: SubscriptionFormValues) => {
    if (cartAbandonQueued) return;
    if (values.pricing.total <= 0) return;

    const validateEmail = (email: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    if (values.email && validateEmail(values.email)) {
      api.queueCartAbandonFollowup(values.email, authenticityToken).then((response) => {
        if (response.queued) {
          setCartAbandonQueued(true);
        }
      });
    }
  };

  return (
    <>
      <div>
        <h1>
          {subscriptionPlan.priceCents > 0 && props.order.pricing.total > 0 ? 'Purchase' : 'Sign up for'} {subscriptionPlan.name}
        </h1>

        {subscriptionPlan.description && <div className="help-block">{subscriptionPlan.description}</div>}
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}>
        {({ values, handleSubmit, setFieldValue, errors }) => {
          const isPaidOrder = values.pricing.total > 0;
          const isInstallments = isPaidOrder && values.paymentStyle == PaymentStyle.INSTALLMENTS;
          const submitButtonName = isPaidOrder ? 'Complete Order' : 'Sign Up';

          const handleCouponCodeApplied = (couponCode) => {
            setIsCalculatingPricing(true);

            return updatePricing(couponCode, values.paymentMethod, zipCodeRef.current).then((newPricing) => {
              // If the response parrots back the code it was successfuly applied
              if (newPricing.couponCode?.toLowerCase() === couponCode?.toLowerCase()) {
                couponCodeRef.current = couponCode;
                setFieldValue('pricing', newPricing);
              }

              setIsCalculatingPricing(false);
            });
          };

          const handlePaymentMethodChanged = (paymentMethod) => {
            // We currently ignore zip code for other payment types, so clear out any value
            if (paymentMethod !== PaymentMethod.STRIPE) {
              setIsCalculatingPricing(true);
              setFieldValue('zipCode', '');
              zipCodeRef.current = '';

              return updatePricing(couponCodeRef.current, values.paymentMethod, '').then((newPricing) => {
                setFieldValue('pricing', newPricing);
                setIsCalculatingPricing(false);
              });
            }
          };

          const handleZipCodeChanged = (zipCode) => {
            zipCodeRef.current = zipCode;
            setIsCalculatingPricing(true);

            return updatePricing(couponCodeRef.current, values.paymentMethod, zipCode).then((newPricing) => {
              setFieldValue('pricing', newPricing);
              setIsCalculatingPricing(false);
            });
          };

          return (
            <Form className={style.formikForm}>
              <ScrollToError />
              {!props.signedIn && <UserFormGroup handleBlur={()=> handleUserFormBlur(values)} /> }

              {isPaidOrder && (
                <>
                  {props.order.allowInstallments && (
                    <InstallmentsFormGroup
                      onChange={(paymentStyle) => {
                        setFieldValue('paymentStyle', paymentStyle);
                        if (paymentStyle !== PaymentStyle.FULLPAYMENT) {
                          setFieldValue('paymentMethod', PaymentMethod.STRIPE);
                        }
                      }}
                      paymentStyle={values.paymentStyle}
                      subscriptionPlan={subscriptionPlan}
                      totalExcludingTax={values.pricing.totalExcludingTax}
                    />
                  )}

                  <PaymentMethodsFormGroup
                    onZipCodeChanged={handleZipCodeChanged}
                    onPaymentMethodChanged={handlePaymentMethodChanged}
                    paymentMethod={values.paymentMethod}
                    paymentRequestMethod={paymentRequestMethod}
                    paymentStyle={values.paymentStyle}
                    totalExcludingTax={values.pricing.totalExcludingTax}
                  />
                </>
              )}

              {subscriptionPlan.priceCents > 0 && (
                <fieldset className="inputs">
                  <div className="form-group">
                    <legend>
                      <span>Confirmation</span>
                    </legend>

                    <Confirmation
                      isCalculatingPricing={isCalculatingPricing}
                      isInstallments={isInstallments}
                      pricingComponents={values.pricing.components}
                      subscriptionPlan={subscriptionPlan}
                    />
                  </div>

                  <div className="u-margin-T-xs">
                    <CouponCodeInput
                      couponCode={values.pricing.couponCode}
                      onApply={handleCouponCodeApplied}
                    />
                  </div>
                </fieldset>
              )}

              {props.isEuUser && <EuTerms buttonName={submitButtonName} />}

              <div className="form-group u-margin-T-s">
                <SubmitPaymentButton
                  isPaidOrder={isPaidOrder}
                  isSubmitting={isSubmitting}
                  onClick={handleSubmit}
                  paymentMethod={values.paymentMethod}
                  paymentRequestMethod={paymentRequestMethod}
                />
              </div>

              {
                isInstallments ? (
                  <InstallmentsTerms
                    buttonName={submitButtonName}
                    installmentInterval={subscriptionPlan.installmentInterval}
                    installmentIntervalCount={subscriptionPlan.installmentIntervalCount}
                    installmentPrice={utils.calculateInstallmentsPrice(values.pricing.total)}
                    isEuUser={props.isEuUser}
                  />
                ) : (
                  !props.isEuUser && <UsTerms buttonName={submitButtonName} />
                )
              }
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export const RegistrationForm: React.FC<SubscriptionFormProps> = (props) => {
  const StripeElementsWrapper = ({ children }) => {
    const stripePromise = loadStripe(props.order.stripeToken);
    return <Elements stripe={stripePromise}>{children}</Elements>;
  };

  return (
    <StripeElementsWrapper>
      <ModalProvider>
        <SubscriptionFormGroups {...props} />
      </ModalProvider>
    </StripeElementsWrapper>
  );
};
RegistrationForm
export default RegistrationForm;
